import React, { useState, useEffect, useCallback } from 'react';

interface TradeStockRecord {
  id: number;
  trade_type: string;
  stock_code: string;
  stock_name: string;
  order_quantity: string;
  order_price: string;
  price: number;
  change_rate: number;
  volume: number;
  collected_at: string;
  trade_timestamp: string;
}

// DB에 저장된 키움 체결 레코드
interface KiwoomOrderRecord {
  id: number;
  ord_dt: string;
  ord_no: string;
  ord_tm: string;
  stk_cd: string;
  stk_nm: string;
  io_tp_nm: string;
  trde_tp: string;
  crd_tp: string;
  ord_qty: string;
  ord_uv: string;
  cntr_qty: string;
  cntr_uv: string;
  ord_remnq: string;
  dmst_stex_tp: string;
  comm_ord_tp: string;
  acpt_tp: string;
  mdfy_cncl: string;
  imported_at: string;
}

interface BtTradeStockHistoryProps {
  title: string;
  /** 선택된 시간 범위 (옵션). 있으면 이 범위에 포함되는 날짜로 키움 체결내역 임포트 */
  startTime?: string;
  endTime?: string;
}

/** 시간 범위(start~end)를 KST 기준 일자 리스트(YYYYMMDD)로 확장 */
function expandDateRangeKST(startTime?: string, endTime?: string): string[] {
  if (!startTime || !endTime) return [];
  const ensureZ = (s: string) =>
    s && !s.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(s) ? s + 'Z' : s;
  const sd = new Date(ensureZ(startTime));
  const ed = new Date(ensureZ(endTime));
  if (Number.isNaN(sd.getTime()) || Number.isNaN(ed.getTime()) || sd > ed) return [];
  const dates: string[] = [];
  // KST(Asia/Seoul)에서 일자 추출. UTC 기준으로 쓰면 자정 직전 거래일이 어긋날 수 있음.
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const cur = new Date(sd);
  // hard cap 31 days to prevent unbounded loops
  for (let i = 0; i < 31 && cur <= ed; i++) {
    const ymd = fmt.format(cur).replace(/-/g, ''); // "20260311"
    if (!dates.includes(ymd)) dates.push(ymd);
    cur.setUTCDate(cur.getUTCDate() + 1);
  }
  // 마지막 날짜도 포함되었는지 확인 (위 루프가 마지막 날 직전에 끝났을 수 있음)
  const lastYmd = fmt.format(ed).replace(/-/g, '');
  if (!dates.includes(lastYmd)) dates.push(lastYmd);
  return dates;
}

const BtTradeStockHistory: React.FC<BtTradeStockHistoryProps> = ({ title, startTime, endTime }) => {
  const [records, setRecords] = useState<TradeStockRecord[]>([]);
  const [kiwoomRecords, setKiwoomRecords] = useState<KiwoomOrderRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [kiwoomLoading, setKiwoomLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [kiwoomError, setKiwoomError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<string | null>(null);
  /** 선택된 시간 범위에서 추출한 일자 리스트 — 임포트 대상 */
  const kiwoomDates = React.useMemo(
    () => expandDateRangeKST(startTime, endTime),
    [startTime, endTime]
  );

  // bt_actual_trade_stock 데이터 로드 (prod에서 임포트한 실제 매매기록, 키움 체결내역과 동일 시간 범위)
  const fetchRecords = useCallback(async () => {
    if (!startTime || !endTime) {
      setRecords([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ start_time: startTime, end_time: endTime });
      const res = await fetch(`/mercury-backend/bt/actual-trade-stock?${params.toString()}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const data: TradeStockRecord[] = json.data || [];
      setRecords(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch');
    } finally {
      setLoading(false);
    }
  }, [startTime, endTime]);

  // DB에서 키움 체결내역 조회
  const fetchKiwoomFromDB = useCallback(async (dates: string[]) => {
    if (dates.length === 0) return;
    setKiwoomLoading(true);
    setKiwoomError(null);
    try {
      const res = await fetch(`/mercury-backend/bt/kiwoom-trading-history?dates=${dates.join(',')}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setKiwoomRecords(json.data || []);
    } catch (err) {
      setKiwoomError(err instanceof Error ? err.message : 'Failed to fetch');
    } finally {
      setKiwoomLoading(false);
    }
  }, []);

  // 키움 API → DB 임포트
  const handleImport = useCallback(async () => {
    if (kiwoomDates.length === 0) {
      setImportResult('임포트할 날짜가 없습니다 (시간 범위 선택 필요)');
      return;
    }
    setImporting(true);
    setImportResult(null);
    setKiwoomError(null);
    try {
      const res = await fetch(`/mercury-backend/bt/kiwoom-trading-history-import?dates=${kiwoomDates.join(',')}`, {
        method: 'POST',
      });
      const json = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(json?.message || json?.error || `HTTP ${res.status}`);
      }
      const errs: string[] = Array.isArray(json?.errors) ? json.errors : [];
      const imported = typeof json?.imported === 'number' ? json.imported : 0;
      // 임포트 후 DB에서 다시 조회 (fetchKiwoomFromDB가 kiwoomError를 초기화하므로 먼저 호출)
      await fetchKiwoomFromDB(kiwoomDates);
      if (errs.length > 0) {
        setKiwoomError(`임포트 실패 (${imported}건 저장됨): ${errs.join(' / ')}`);
      } else {
        setImportResult(`${imported}건 임포트 완료 (${kiwoomDates.join(', ')})`);
      }
    } catch (err) {
      setKiwoomError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setImporting(false);
    }
  }, [kiwoomDates, fetchKiwoomFromDB]);

  // 초기 로드: 오른쪽 표(bt_trade_stock) 한 번 로드
  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  // 시간 범위가 바뀔 때마다 키움 체결 DB 재조회
  useEffect(() => {
    if (kiwoomDates.length > 0) fetchKiwoomFromDB(kiwoomDates);
    else setKiwoomRecords([]);
  }, [kiwoomDates, fetchKiwoomFromDB]);

  /** 선택된 시간 범위 내 키움 체결만 표시 (kt00007은 일자 단위로 받기 때문에 화면 단에서 필터) */
  const filteredKiwoomRecords = React.useMemo(() => {
    if (!startTime || !endTime) return kiwoomRecords;
    const ensureZ = (s: string) =>
      s && !s.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(s) ? s + 'Z' : s;
    const startMs = Date.parse(ensureZ(startTime));
    const endMs = Date.parse(ensureZ(endTime));
    if (Number.isNaN(startMs) || Number.isNaN(endMs)) return kiwoomRecords;
    return kiwoomRecords.filter((r) => {
      // ord_dt: "20260311" (KST 거래일), ord_tm: "09:00:40" (KST)
      if (!r.ord_dt || r.ord_dt.length !== 8) return false;
      if (!r.ord_tm) return false;
      const ymd = `${r.ord_dt.slice(0, 4)}-${r.ord_dt.slice(4, 6)}-${r.ord_dt.slice(6, 8)}`;
      const isoKST = `${ymd}T${r.ord_tm}+09:00`;
      const t = Date.parse(isoKST);
      if (Number.isNaN(t)) return false;
      return t >= startMs && t <= endMs;
    });
  }, [kiwoomRecords, startTime, endTime]);

  const formatTime = (t: string) => {
    if (!t) return '-';
    return t.replace('T', ' ').replace('Z', '').slice(0, 19);
  };

  const parseQty = (s: string) => parseInt(s, 10) || 0;
  const parsePrice = (s: string) => parseInt(s, 10) || 0;
  const cleanStockCode = (code: string) => code.replace(/^A/, '');

  /** bt_actual_trade_stock 매수→매도 페어 수익률 합 ((sell - buy) / buy * 100, order_price 우선; analy 페이지의 totalActualProfit과 동일 계산) */
  const totalActualProfit = React.useMemo(() => {
    const byStock = new Map<string, TradeStockRecord[]>();
    for (const r of records) {
      const sc = String(r.stock_code ?? '');
      if (!sc) continue;
      if (!byStock.has(sc)) byStock.set(sc, []);
      byStock.get(sc)!.push(r);
    }
    let total = 0;
    let pairCount = 0;
    for (const list of Array.from(byStock.values())) {
      list.sort((a, b) => (a.trade_timestamp ?? a.collected_at).localeCompare(b.trade_timestamp ?? b.collected_at));
      let pendingBuy: TradeStockRecord | null = null;
      for (const r of list) {
        const isBuy = String(r.trade_type).toUpperCase() === 'BUY';
        if (isBuy) {
          pendingBuy = r;
        } else if (pendingBuy) {
          const bp = pendingBuy.order_price && pendingBuy.order_price !== '' ? Number(pendingBuy.order_price) : Number(pendingBuy.price);
          const sp = r.order_price && r.order_price !== '' ? Number(r.order_price) : Number(r.price);
          if (Number.isFinite(bp) && Number.isFinite(sp) && bp !== 0) {
            total += ((sp - bp) / bp) * 100;
            pairCount++;
          }
          pendingBuy = null;
        }
      }
    }
    return { total, pairCount };
  }, [records]);

  /** kt00007 매수/매도 페어 수익률 합 ((sell - buy) / buy * 100, 종목별 시간순 짝지음) */
  const kiwoomPairProfit = React.useMemo(() => {
    const byStock = new Map<string, KiwoomOrderRecord[]>();
    for (const r of filteredKiwoomRecords) {
      const sc = String(r.stk_cd ?? '');
      if (!sc) continue;
      if (!byStock.has(sc)) byStock.set(sc, []);
      byStock.get(sc)!.push(r);
    }
    let totalProfit = 0;
    let pairCount = 0;
    for (const list of Array.from(byStock.values())) {
      // ord_dt+ord_tm 오름차순 정렬 (BUY → SELL 순서로 짝짓기 위함)
      list.sort((a, b) => {
        const k = a.ord_dt.localeCompare(b.ord_dt);
        return k !== 0 ? k : a.ord_tm.localeCompare(b.ord_tm);
      });
      let pendingBuy: KiwoomOrderRecord | null = null;
      for (const r of list) {
        const isBuy = String(r.io_tp_nm ?? '').includes('매수');
        const isSell = String(r.io_tp_nm ?? '').includes('매도');
        if (isBuy) {
          pendingBuy = r;
        } else if (isSell && pendingBuy) {
          const bp = parsePrice(pendingBuy.cntr_uv);
          const sp = parsePrice(r.cntr_uv);
          if (bp > 0 && sp > 0) {
            totalProfit += ((sp - bp) / bp) * 100;
            pairCount++;
          }
          pendingBuy = null;
        }
      }
    }
    return { totalProfit, pairCount };
  }, [filteredKiwoomRecords]);

  return (
    <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
        <h4 style={{ margin: 0, color: '#495057' }}>
          {title}
        </h4>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {importResult && (
            <span style={{ fontSize: '12px', color: '#198754' }}>{importResult}</span>
          )}
          <button
            onClick={handleImport}
            disabled={importing || kiwoomDates.length === 0}
            style={{
              padding: '4px 12px',
              borderRadius: '4px',
              border: '1px solid #0d6efd',
              backgroundColor: importing ? '#ccc' : '#0d6efd',
              color: '#fff',
              fontSize: '12px',
              cursor: (importing || kiwoomDates.length === 0) ? 'not-allowed' : 'pointer',
            }}
          >
            {importing ? '임포트중...' : '키움 체결내역 임포트'}
          </button>
          <button
            onClick={() => { fetchRecords(); if (kiwoomDates.length > 0) fetchKiwoomFromDB(kiwoomDates); }}
            disabled={loading || kiwoomLoading}
            style={{
              padding: '4px 12px',
              borderRadius: '4px',
              border: '1px solid #ced4da',
              backgroundColor: '#fff',
              fontSize: '12px',
              cursor: (loading || kiwoomLoading) ? 'not-allowed' : 'pointer',
            }}
          >
            {(loading || kiwoomLoading) ? '로딩중...' : '새로고침'}
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '15px' }}>
        {/* 왼쪽: 키움 실제 체결내역 (DB) */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <h5 style={{ margin: '0 0 8px 0', color: '#0d6efd', fontSize: '13px' }}>
            키움 실제 체결내역 (kt00007)
            <span style={{ fontWeight: 'normal', color: '#6c757d', marginLeft: '8px' }}>
              {startTime && endTime
                ? `(${filteredKiwoomRecords.length}건 / 일자 전체 ${kiwoomRecords.length}건)`
                : `(${kiwoomRecords.length}건)`}
            </span>
            {kiwoomPairProfit.pairCount > 0 && (
              <span
                style={{
                  fontWeight: 'normal',
                  marginLeft: '8px',
                  color: kiwoomPairProfit.totalProfit > 0 ? '#dc3545' : kiwoomPairProfit.totalProfit < 0 ? '#0d6efd' : '#495057',
                }}
                title={`매수→매도 페어 ${kiwoomPairProfit.pairCount}건의 체결단가 기준 수익률 합`}
              >
                수익률 합 {kiwoomPairProfit.totalProfit > 0 ? '+' : ''}{kiwoomPairProfit.totalProfit.toFixed(2)}% ({kiwoomPairProfit.pairCount} 페어)
              </span>
            )}
          </h5>
          {kiwoomError && (
            <div style={{ padding: '8px', backgroundColor: '#fff5f5', color: '#dc3545', borderRadius: '4px', fontSize: '13px', marginBottom: '8px' }}>
              {kiwoomError}
            </div>
          )}
          {filteredKiwoomRecords.length === 0 && !kiwoomLoading && !kiwoomError ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#6c757d', fontSize: '13px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              {kiwoomRecords.length === 0
                ? '키움 체결 데이터가 없습니다. "키움 체결내역 임포트" 버튼을 눌러주세요.'
                : '선택된 시간 범위 내 체결 없음 (일자 전체로는 ' + kiwoomRecords.length + '건 있음).'}
            </div>
          ) : (
            <div style={{ maxHeight: '400px', overflow: 'auto', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#d0e7ff', position: 'sticky', top: 0 }}>
                    <th style={thStyle}>날짜</th>
                    <th style={thStyle}>시간</th>
                    <th style={thStyle}>매매</th>
                    <th style={thStyle}>종목코드</th>
                    <th style={thStyle}>종목명</th>
                    <th style={thStyle}>체결수량</th>
                    <th style={thStyle}>체결단가</th>
                    <th style={thStyle}>주문수량</th>
                    <th style={thStyle}>거래소</th>
                    <th style={thStyle}>통신구분</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredKiwoomRecords.map((r) => {
                    const isBuy = r.io_tp_nm.includes('매수');
                    return (
                      <tr key={r.id} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ ...tdStyle, fontSize: '11px' }}>{r.ord_dt}</td>
                        <td style={{ ...tdStyle, fontSize: '11px' }}>{r.ord_tm}</td>
                        <td style={{
                          ...tdStyle,
                          color: isBuy ? '#dc3545' : '#0d6efd',
                          fontWeight: 600,
                        }}>
                          {r.io_tp_nm}
                        </td>
                        <td style={tdStyle}>{cleanStockCode(r.stk_cd)}</td>
                        <td style={tdStyle}>{r.stk_nm}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{parseQty(r.cntr_qty)}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{parsePrice(r.cntr_uv).toLocaleString()}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{parseQty(r.ord_qty)}</td>
                        <td style={tdStyle}>{r.dmst_stex_tp}</td>
                        <td style={tdStyle}>{r.comm_ord_tp}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* 오른쪽: 기존 bt_trade_stock (DB) */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <h5 style={{ margin: '0 0 8px 0', color: '#495057', fontSize: '13px' }}>
            실제 매매기록 (bt_actual_trade_stock)
            <span style={{ fontWeight: 'normal', color: '#6c757d', marginLeft: '8px' }}>
              ({records.length}건)
            </span>
            {totalActualProfit.pairCount > 0 && (
              <span
                style={{
                  fontWeight: 'normal',
                  marginLeft: '8px',
                  color: totalActualProfit.total > 0 ? '#dc3545' : totalActualProfit.total < 0 ? '#0d6efd' : '#495057',
                }}
                title={`매수→매도 페어 ${totalActualProfit.pairCount}건의 (sell - buy) / buy 수익률 합 (order_price 우선). analy 페이지의 실제 거래 수치와 동일.`}
              >
                수익률 합 {totalActualProfit.total > 0 ? '+' : ''}{totalActualProfit.total.toFixed(2)}% ({totalActualProfit.pairCount} 페어)
              </span>
            )}
          </h5>
          {error && (
            <div style={{ padding: '8px', backgroundColor: '#fff5f5', color: '#dc3545', borderRadius: '4px', fontSize: '13px', marginBottom: '8px' }}>
              {error}
            </div>
          )}
          {records.length === 0 && !loading && !error ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#6c757d', fontSize: '13px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              선택된 기간에 bt_actual_trade_stock 데이터가 없습니다. "데이터 임포트 (prod to dev bt)" 버튼으로 임포트하세요.
            </div>
          ) : (
            <div style={{ maxHeight: '400px', overflow: 'auto', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#e9ecef', position: 'sticky', top: 0 }}>
                    <th style={thStyle}>매매</th>
                    <th style={thStyle}>종목코드</th>
                    <th style={thStyle}>종목명</th>
                    <th style={thStyle}>수량</th>
                    <th style={thStyle}>주문가</th>
                    <th style={thStyle}>현재가</th>
                    <th style={thStyle}>등락률</th>
                    <th style={thStyle}>수집시간</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((r) => (
                    <tr key={r.id} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{
                        ...tdStyle,
                        color: r.trade_type === 'BUY' ? '#dc3545' : '#0d6efd',
                        fontWeight: 600,
                      }}>
                        {r.trade_type}
                      </td>
                      <td style={tdStyle}>{r.stock_code}</td>
                      <td style={tdStyle}>{r.stock_name}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{r.order_quantity}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{r.order_price || '-'}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{r.price?.toLocaleString()}</td>
                      <td style={{
                        ...tdStyle,
                        textAlign: 'right',
                        color: r.change_rate > 0 ? '#dc3545' : r.change_rate < 0 ? '#0d6efd' : '#495057',
                      }}>
                        {r.change_rate > 0 ? '+' : ''}{r.change_rate?.toFixed(2)}%
                      </td>
                      <td style={{ ...tdStyle, fontSize: '11px' }}>{formatTime(r.collected_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const thStyle: React.CSSProperties = {
  padding: '6px 8px',
  textAlign: 'left',
  fontWeight: 600,
  borderBottom: '2px solid #dee2e6',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '5px 8px',
  whiteSpace: 'nowrap',
  color: '#000',
};

export default BtTradeStockHistory;
